// Enhanced Navigation Provider with API integration
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { FuseFlatNavItemType, FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';
import FuseNavItemModel from '@fuse/core/FuseNavigation/models/FuseNavItemModel';
import { PartialDeep } from 'type-fest';
import { NavigationContext } from '@/components/theme-layouts/components/navigation/contexts/NavigationContext';
import { useSafeNavigation } from '@/api/hooks/useSafeNavigation';
import useJwtAuth from '@auth/services/jwt/useJwtAuth';

// Transform API navigation URLs to match route structure
function transformApiNavigation(navigation: FuseNavItemType[]): FuseNavItemType[] {
	return navigation
		.filter((item) => item && item.id && item.title) // Filter out invalid items
		.map((item) => {
			const transformedItem = { ...item };

			// Transform URL if it exists
			if (transformedItem.url && transformedItem.url !== 'undefined') {
				// Remove /control-panel prefix if present
				transformedItem.url = transformedItem.url.replace(/^\/control-panel/, '');

				// Ensure it starts with /
				if (transformedItem.url && !transformedItem.url.startsWith('/')) {
					transformedItem.url = '/' + transformedItem.url;
				}
			} else if (transformedItem.url === 'undefined') {
				// Remove undefined URLs
				delete transformedItem.url;
			}

			// Clean up icon if it's undefined
			if (transformedItem.icon === 'undefined' || !transformedItem.icon) {
				delete transformedItem.icon;
			}

			// Clean up title if it's undefined
			if (transformedItem.title === 'undefined') {
				transformedItem.title = 'Untitled';
			}

			// Recursively transform children
			if (transformedItem.children) {
				transformedItem.children = transformApiNavigation(transformedItem.children);
			}

			return transformedItem;
		});
}

export function NavigationContextProvider({ children }: { children: ReactNode }) {
	const { isAuthenticated } = useJwtAuth();

	// Use navigation hook - API only, no fallback
	const {
		data: apiNavigation,
		isLoading: isLoadingNav,
		error: navError,
		refetch: refetchNavigation
	} = useSafeNavigation({ enabled: isAuthenticated });

	// Start with empty navigation, only use API data
	const [navigationItems, setNavigationItems] = useState<FuseFlatNavItemType[]>([]);

	/**
	 * Update navigation items based on authentication state and API data
	 */
	useEffect(() => {
		if (isAuthenticated && apiNavigation && Array.isArray(apiNavigation) && !navError) {
			try {
				// Transform URLs to match route structure
				const transformedItems = transformApiNavigation(apiNavigation);

				// Store flattened navigation data to match FUSE expectations
				const flattenedItems = FuseNavigationHelper.flattenNavigation(transformedItems);
				setNavigationItems(flattenedItems);
			} catch {
				setNavigationItems([]);
			}
		} else if (navError) {
			setNavigationItems([]);
		} else if (isLoadingNav) {
			// Loading navigation from API
			// Keep current state while loading
		} else {
			setNavigationItems([]);
		}
	}, [isAuthenticated, apiNavigation, navError, isLoadingNav]);
	const setNavigation = useCallback((items: FuseNavItemType[]) => {
		setNavigationItems(FuseNavigationHelper.flattenNavigation(items)); // Store flattened data
	}, []);

	const appendNavigationItem = useCallback(
		(item: FuseNavItemType, parentId?: string | null) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.appendNavItem(navigation, FuseNavItemModel(item), parentId));
		},
		[navigationItems, setNavigation]
	);

	const prependNavigationItem = useCallback(
		(item: FuseNavItemType, parentId?: string | null) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.prependNavItem(navigation, FuseNavItemModel(item), parentId));
		},
		[navigationItems, setNavigation]
	);

	const updateNavigationItem = useCallback(
		(id: string, item: PartialDeep<FuseNavItemType>) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.updateNavItem(navigation, id, item));
		},
		[navigationItems, setNavigation]
	);

	const removeNavigationItem = useCallback(
		(id: string) => {
			const navigation = FuseNavigationHelper.unflattenNavigation(navigationItems);
			setNavigation(FuseNavigationHelper.removeNavItem(navigation, id));
		},
		[navigationItems, setNavigation]
	);

	const resetNavigation = useCallback(() => {
		// Reset by refetching API navigation
		if (isAuthenticated && !navError) {
			refetchNavigation();
		} else {
			setNavigationItems([]);
		}
	}, [isAuthenticated, navError, refetchNavigation]);

	const getNavigationItemById = useCallback(
		(id: string) => navigationItems.find((item) => item.id === id),
		[navigationItems]
	);

	const value = {
		setNavigation,
		navigationItems,
		appendNavigationItem,
		prependNavigationItem,
		updateNavigationItem,
		removeNavigationItem,
		resetNavigation,
		getNavigationItemById,
		// Navigation state information
		isLoadingNavigation: isLoadingNav,
		navigationError: navError,
		hasApiNavigation: !!(isAuthenticated && apiNavigation && Array.isArray(apiNavigation) && !navError),
		refreshNavigation: refetchNavigation
	};

	return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}
